import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import { Modal, ModalClose } from './primitives/Modal.jsx';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const TYPES = ['Bug', 'UX', 'Feature', 'Data'];

const TYPE_DESCRIPTIONS = {
  Bug: 'Something is broken',
  UX: 'Hard to use or confusing',
  Feature: 'Something I want added',
  Data: 'Wrong or missing data',
};

export default function FeedbackWidget() {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('Bug');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const openWidget = useCallback(() => {
    setOpen(true);
    setResult(null);
    setError(null);
    setDescription('');
    setType('Bug');
    setScreenshot(null);
  }, []);

  const handleFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshot(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  }, []);

  const submit = useCallback(async () => {
    if (!description.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = getToken ? await getToken() : null;
      const res = await fetch(`${API_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type,
          description: description.trim(),
          url: window.location.href,
          screenshot: screenshot || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.error?.message || `Request failed (${res.status})`);
      }
      setResult(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }, [description, type, screenshot, submitting, getToken]);

  const close = useCallback(() => {
    setOpen(false);
    setResult(null);
    setError(null);
  }, []);

  return (
    <>
      {/* Floating launcher — always visible */}
      <button
        id="feedback-widget"
        onClick={openWidget}
        title="Send feedback"
        className="fixed bottom-6 right-6 w-10 h-10 rounded-full bg-sitruna-yellow text-sitruna-black shadow-lg flex items-center justify-center hover:scale-105 transition-transform text-sm font-bold"
        style={{ zIndex: 'var(--z-feedback-floater)' }}
      >
        ?
      </button>

      <Modal open={open} onOpenChange={(v) => { if (!v) close(); }} title="Send feedback" size="sm">
        {result ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-status-ok-bg flex items-center justify-center shrink-0 text-status-ok text-sm font-bold">
                ✓
              </div>
              <div>
                <p className="text-sm font-semibold text-n-800">Feedback received</p>
                <p className="text-xs text-n-500 mt-0.5">
                  Triaged as{' '}
                  <span className="text-sitruna-black font-semibold">
                    {result.triage.priority_label}
                  </span>{' '}
                  · <span className="text-n-700">{result.triage.category}</span>
                </p>
                <p className="text-xs text-n-500 mt-1 leading-relaxed">{result.triage.summary}</p>
              </div>
            </div>
            <div className="flex gap-2">
              {result.issue_url && (
                <a
                  href={result.issue_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 text-center text-xs bg-n-100 hover:bg-n-150 text-n-700 rounded-lg py-2 transition-colors"
                >
                  View issue #{result.issue_number}
                </a>
              )}
              <ModalClose>
                <button type="button" className="flex-1 text-xs bg-sitruna-yellow hover:brightness-95 text-sitruna-black font-semibold rounded-lg py-2 transition-colors">
                  Done
                </button>
              </ModalClose>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Type selector */}
            <div className="grid grid-cols-4 gap-1">
              {TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  title={TYPE_DESCRIPTIONS[t]}
                  className={`text-xs rounded-lg py-1.5 font-medium transition-colors ${
                    type === t
                      ? 'bg-sitruna-yellow text-sitruna-black'
                      : 'bg-n-100 text-n-600 hover:bg-n-150 hover:text-n-800'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Description */}
            <textarea
              autoFocus
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === 'Bug' ? 'What went wrong? What did you expect instead?'
                : type === 'UX' ? 'What was confusing or hard to use?'
                : type === 'Feature' ? 'What would you like added?'
                : 'What data looks wrong or is missing?'
              }
              rows={4}
              className="w-full bg-n-50 border border-n-200 rounded-lg text-sm text-n-800 placeholder-n-400 px-3 py-2 resize-none focus:outline-none focus:border-sitruna-yellow"
            />

            {/* Screenshot */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
            />
            <div className="rounded-lg overflow-hidden border border-n-200 bg-n-50 relative" style={{ height: 80 }}>
              {screenshot ? (
                <>
                  <img src={screenshot} alt="Screenshot" className="w-full h-full object-cover object-top" />
                  <button
                    type="button"
                    onClick={() => setScreenshot(null)}
                    className="absolute top-1.5 right-1.5 text-[10px] bg-black/60 hover:bg-black/80 text-white px-2 py-0.5 rounded"
                  >
                    Remove
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute inset-0 w-full flex items-center justify-center gap-1.5 text-xs text-n-400 hover:text-n-600 hover:bg-n-100 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <polyline points="21 15 16 10 5 21" />
                  </svg>
                  Attach screenshot
                </button>
              )}
            </div>

            {error && <p className="text-xs text-status-bad">{error}</p>}

            <button
              type="button"
              onClick={submit}
              disabled={!description.trim() || submitting}
              className="w-full text-sm bg-sitruna-yellow hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed text-sitruna-black font-semibold rounded-lg py-2 transition-colors"
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </button>

            <p className="text-[10px] text-n-400 text-center">
              Creates a GitHub issue · Claude triages priority automatically
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
